import React, { useState, useMemo } from "react";
import { TbSearch, TbPencil, TbTrash, TbPlus, TbAlertTriangle } from "react-icons/tb";
import { useThresholds } from "../../hooks/useThresholds";
import { Alert, EmptyState, SkeletonRows, Chip, Button } from "../../components";
import { TIERS, TIER_TONE, describeRule } from "../../lib/thresholds";
import ThresholdDrawer from "./ThresholdDrawer";
import { NewCategoryModal, GroupModal, DeleteConfirm } from "./ThresholdCatalogue";
import { CategoryIcon } from "./IconPicker";
import "./Settings.css";

// Risk thresholds.
//
// The bands that decide how every scan in this workspace is graded. Seventy categories
// across nine scan groups, each with a Good, Moderate and Bad band.
//
// Two things shape this screen. First, it is read far more often than it is written —
// most visits are someone checking what a category is currently banded at, so the bands
// are rendered in the table in plain English rather than hidden behind a click. Second,
// every category always has a value: the API layers this workspace's overrides over the
// global defaults and returns a resolved set either way, so there is no empty state per
// row, only a "customised or default" distinction.
//
// Two scopes live on this screen, and the difference is the thing to keep straight.
// Re-banding is per workspace: it writes tenant_category_thresholds, keyed by tenant.
// Adding, moving or deleting a category or a scan group is NOT — those tables carry no
// tenant_id, so the catalogue is shared by every workspace on the installation. The
// catalogue controls are admin-only and each says so before it acts.

const VIEWS = [
  { value: "", label: "All" },
  { value: "custom", label: "Customised" },
  { value: "default", label: "Default" },
];

const ThresholdsScreen = ({ isAdmin }) => {
  const {
    categories, groups, loading, error, save, reset,
    createCategory, updateCategory, moveCategory, deleteCategory,
    createGroup, updateGroup, deleteGroup,
  } = useThresholds();

  const [search, setSearch] = useState("");
  const [mode, setMode] = useState("");
  const [group, setGroup] = useState("");
  const [editing, setEditing] = useState(null);

  // Catalogue dialogs. Each holds the subject it acts on, so closing is just clearing it.
  const [newCategoryFor, setNewCategoryFor] = useState(null);   // "" = no group preselected
  const [groupModal, setGroupModal] = useState(null);           // { group } or {} for new
  const [confirm, setConfirm] = useState(null);

  // Group order comes from the scan_groups table so it matches the order scans run in.
  // Anything whose scan_type is not in that table still has to appear, or a category
  // would silently vanish from the only screen that can band it.
  const groupNames = useMemo(() => {
    const known = groups.map((g) => g.key);
    const seen = [...new Set(categories.map((c) => c.scan_type).filter(Boolean))];
    return [...known.filter((k) => seen.includes(k)), ...seen.filter((s) => !known.includes(s))];
  }, [groups, categories]);

  const labelFor = useMemo(() => {
    const map = new Map(groups.map((g) => [g.key, g.label]));
    return (key) => map.get(key) || key;
  }, [groups]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return categories.filter((c) => {
      if (mode && (c.mode || "default") !== mode) return false;
      if (group && c.scan_type !== group) return false;
      if (!q) return true;
      return (
        (c.name || "").toLowerCase().includes(q) ||
        (c.metric || "").toLowerCase().includes(q) ||
        (c.description || "").toLowerCase().includes(q)
      );
    });
  }, [categories, search, mode, group]);

  // Sections rather than one flat table: a band is only meaningful next to the other
  // bands in its scan, and seventy undifferentiated rows hides that entirely.
  // An empty group is now a real state — someone can create one before filing anything
  // in it — so a group with no rows is still rendered, as long as no filter is narrowing
  // the view. Hiding it would make a group someone just created appear not to exist.
  const narrowing = !!(search || mode || group);
  const sections = useMemo(
    () => groupNames
      .map((name) => ({ name, rows: filtered.filter((c) => c.scan_type === name) }))
      .filter((sec) => sec.rows.length > 0 || !narrowing),
    [groupNames, filtered, narrowing]
  );

  const customCount = categories.filter((c) => c.mode === "custom").length;

  // The drawer renders from the list, so it follows a save without being told to.
  const open = editing ? categories.find((c) => c.id === editing) || null : null;

  const groupFor = (key) => groups.find((g) => g.key === key) || null;

  // Deleting a seeded category is refused, not merely warned about. findingsGenerator
  // resolves a finding to its category BY NAME, so removing one stops those findings
  // scoring for every workspace — and seedCategories re-inserts it on the next service
  // boot, so the delete quietly undoes itself and the damage looks intermittent.
  //
  // Fails safe on `!== false` rather than on truthiness: a service that predates the
  // is_seeded flag omits the field entirely, and reading that absence as "not seeded"
  // would unlock the delete on exactly the categories it exists to protect.
  const categoryConfirm = (c) => {
    const seeded = c.is_seeded !== false;
    return {
    title: `Delete ${c.name}?`,
    danger: seeded,
    confirmLabel: "Delete category",
    onConfirm: () => deleteCategory(c.id),
    body: seeded ? (
      <div className="v2-thr-warn">
        <TbAlertTriangle size={15} />
        <div>
          <b>This category ships with the product</b>
          <div>
            The scanner matches findings to it by name, so deleting it would stop those
            findings scoring for every workspace — and it would be recreated the next time
            the service restarts. Change its bands instead.
          </div>
        </div>
      </div>
    ) : (
      <>
        <p className="v2-thr-desc">
          Removes <b>{c.name}</b> from every workspace on this installation, along with any
          bands other workspaces have set for it. This cannot be undone.
        </p>
        <p className="v2-ghl-help">Scans already stored keep their results; only future scoring changes.</p>
      </>
    ),
    };
  };

  // System groups are refused server-side, so the dialog says so rather than offering a
  // button that returns 403.
  const groupConfirm = (key) => {
    const g = groupFor(key);
    const count = categories.filter((c) => c.scan_type === key).length;
    return {
      title: `Delete ${g.label}?`,
      danger: g.is_system === 1,
      confirmLabel: "Delete group",
      onConfirm: () => deleteGroup(g.id),
      body: g.is_system === 1 ? (
        <div className="v2-thr-warn">
          <TbAlertTriangle size={15} />
          <div>
            <b>System groups cannot be deleted</b>
            <div>This group ships with the product. Its name and colour can still be changed.</div>
          </div>
        </div>
      ) : (
        <>
          <p className="v2-thr-desc">
            Removes <b>{g.label}</b> from every workspace on this installation.
          </p>
          <p className="v2-ghl-help">
            {count === 0
              ? "No categories are filed under it."
              : `Its ${count} ${count === 1 ? "category becomes" : "categories become"} uncategorised rather than being deleted — bands and history are kept.`}
          </p>
        </>
      ),
    };
  };

  return (
    <div className="v2-page">
      <div className="v2-pagehead">
        <div>
          <h2>Risk thresholds</h2>
          <p>The bands that decide how each scan is scored. Changes apply to this workspace only.</p>
        </div>
        <div className="v2-pagehead-actions">
          <Chip><b>{customCount}</b> customised</Chip>
          {isAdmin && (
            <>
              <Button onClick={() => setGroupModal({})}>
                <TbPlus size={14} style={{ verticalAlign: "-2px" }} /> New group
              </Button>
              <Button variant="primary" onClick={() => setNewCategoryFor("")}>
                <TbPlus size={14} style={{ verticalAlign: "-2px" }} /> New category
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="v2-views">
        {VIEWS.map((v) => (
          <button key={v.value || "all"} type="button"
                  className={`v2-view${mode === v.value ? " on" : ""}`}
                  aria-current={mode === v.value ? "page" : undefined}
                  onClick={() => setMode(v.value)}>
            {v.label}
            <span className="n">
              {v.value ? categories.filter((c) => (c.mode || "default") === v.value).length : categories.length}
            </span>
          </button>
        ))}
      </div>

      <div className="v2-filters">
        <div className="v2-omni">
          <TbSearch size={14} />
          <input type="search" value={search} placeholder="Search categories"
                 aria-label="Search categories"
                 onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select className="v2-input v2-cadence" value={group}
                aria-label="Filter by scan group"
                onChange={(e) => setGroup(e.target.value)}>
          <option value="">All scan groups</option>
          {groupNames.map((g) => <option key={g} value={g}>{labelFor(g)}</option>)}
        </select>
      </div>

      <Alert>{error}</Alert>

      {loading ? (
        <div className="v2-tablewrap">
          <table className="v2-table">
            <tbody><SkeletonRows rows={6} cols={6} widths={["28%", "34%", "18%", "18%", "18%", "12%"]} /></tbody>
          </table>
        </div>
      ) : sections.length === 0 ? (
        <EmptyState title={search || mode || group ? "No matches" : "No categories"}>
          {search || mode || group
            ? "Nothing matches these filters."
            : "The scan categories have not been seeded yet."}
        </EmptyState>
      ) : (
        sections.map((section) => (
          <section key={section.name} className="v2-thr-section">
            <h3 className="v2-thr-groupname">
              {groupFor(section.name)?.icon && (
                <span className="v2-thr-groupmark"
                      style={{ background: groupFor(section.name)?.color || "var(--v2-text-muted)" }}>
                  <CategoryIcon name={groupFor(section.name).icon} size={12} />
                </span>
              )}
              {labelFor(section.name)}
              <span className="v2-thr-groupcount">{section.rows.length}</span>
              {isAdmin && (
                <span className="v2-thr-groupactions">
                  <button type="button" className="v2-linkcell"
                          aria-label={`Add a category to ${labelFor(section.name)}`}
                          onClick={() => setNewCategoryFor(section.name)}>
                    <TbPlus size={13} /> Category
                  </button>
                  {groupFor(section.name) && (
                    <>
                      <button type="button" className="v2-linkcell"
                              aria-label={`Edit ${labelFor(section.name)}`}
                              onClick={() => setGroupModal({ group: groupFor(section.name) })}>
                        <TbPencil size={13} /> Edit
                      </button>
                      <button type="button" className="v2-linkcell v2-linkcell--danger"
                              aria-label={`Delete ${labelFor(section.name)}`}
                              onClick={() => setConfirm(groupConfirm(section.name))}>
                        <TbTrash size={13} /> Delete
                      </button>
                    </>
                  )}
                </span>
              )}
            </h3>

            <div className="v2-tablewrap">
              <table className="v2-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Measures</th>
                    {TIERS.map((t) => <th key={t}>{t}</th>)}
                    <th style={{ width: 90 }} />
                  </tr>
                </thead>
                <tbody>
                  {section.rows.map((c) => (
                    <tr key={c.id} className="v2-thr-row" onClick={() => setEditing(c.id)}
                        tabIndex={0} role="button"
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setEditing(c.id); }
                        }}>
                      <td>
                        <span className="v2-thr-name">
                          <CategoryIcon name={c.icon} size={14} className="v2-thr-icon" />
                          {c.name}
                        </span>
                        {c.mode === "custom" && (
                          <span className="v2-rbadge v2-rbadge--accent v2-thr-tag">CUSTOM</span>
                        )}
                      </td>
                      <td className="v2-dim">{c.metric || "—"}</td>
                      {TIERS.map((t) => (
                        <td key={t}>
                          <span className={`v2-thr-band v2-thr-band--${TIER_TONE[t]}`}>
                            {describeRule(c.rules?.[t])}
                          </span>
                        </td>
                      ))}
                      <td className="v2-thr-rowactions">
                        {isAdmin && (
                          <button type="button" className="v2-linkcell v2-linkcell--danger"
                                  aria-label={`Delete ${c.name}`}
                                  onClick={(e) => { e.stopPropagation(); setConfirm(categoryConfirm(c)); }}>
                            <TbTrash size={13} />
                          </button>
                        )}
                        <button type="button" className="v2-linkcell"
                                aria-label={`Edit thresholds for ${c.name}`}
                                onClick={(e) => { e.stopPropagation(); setEditing(c.id); }}>
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))
      )}

      {open && (
        <ThresholdDrawer
          category={open}
          groups={groups}
          isAdmin={isAdmin}
          onClose={() => setEditing(null)}
          onSave={save}
          onReset={reset}
          onMove={moveCategory}
          onUpdate={updateCategory}
        />
      )}

      {newCategoryFor !== null && (
        <NewCategoryModal
          groups={groups}
          defaultGroup={newCategoryFor}
          onClose={() => setNewCategoryFor(null)}
          onCreate={createCategory}
        />
      )}

      {groupModal && (
        <GroupModal
          group={groupModal.group}
          onClose={() => setGroupModal(null)}
          onSave={(fields) => (groupModal.group
            ? updateGroup(groupModal.group.id, fields)
            : createGroup(fields))}
        />
      )}

      {confirm && (
        <DeleteConfirm {...confirm} onClose={() => setConfirm(null)} />
      )}
    </div>
  );
};

export default ThresholdsScreen;
